/**
 * EditModeContext - State management for dashboard editing
 *
 * Manages:
 * - Edit mode state (on/off)
 * - Current dashboard being edited
 * - Layout changes (card reordering)
 * - Card configuration changes
 * - Save/cancel/reset operations
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  updateCardConfig,
  updateCardLayout,
  resetDashboardCustomizations,
  getCustomizedDashboard
} from '../services/cardConfigService';

const EditModeContext = createContext(null);

export const useEditMode = () => {
  const context = useContext(EditModeContext);
  if (!context) {
    throw new Error('useEditMode must be used within an EditModeProvider');
  }
  return context;
};

export const EditModeProvider = ({ children, onSaveComplete }) => {
  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentDashboardId, setCurrentDashboardId] = useState(null);
  const [templateId, setTemplateId] = useState(null);

  // Changes tracking
  const [layoutChanges, setLayoutChanges] = useState(null); // { card_order: [], template_id: '' }
  const [cardChanges, setCardChanges] = useState({}); // { cardId: chartDefinition }
  const [originalConfig, setOriginalConfig] = useState(null);

  // Operation state
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Check if there are unsaved changes
   */
  const hasUnsavedChanges = () => {
    return layoutChanges !== null || Object.keys(cardChanges).length > 0;
  };

  /**
   * Enter edit mode for a dashboard
   */
  const enterEditMode = useCallback((dashboardId, dashboardTemplateId, config) => {
    setIsEditMode(true);
    setCurrentDashboardId(dashboardId);
    setTemplateId(dashboardTemplateId);
    setOriginalConfig(JSON.parse(JSON.stringify(config))); // Deep copy
    setLayoutChanges(null);
    setCardChanges({});
    setError(null);
  }, []);

  /**
   * Exit edit mode (with optional save)
   */
  const exitEditMode = useCallback(async (save = false) => {
    if (save && hasUnsavedChanges()) {
      await saveChanges();
    }

    // Reset state
    setIsEditMode(false);
    setCurrentDashboardId(null);
    setTemplateId(null);
    setLayoutChanges(null);
    setCardChanges({});
    setOriginalConfig(null);
    setError(null);
  }, [hasUnsavedChanges]);

  /**
   * Update layout (card order)
   */
  const updateLayout = useCallback((newCardOrder) => {
    setLayoutChanges({
      card_order: newCardOrder,
      template_id: templateId
    });
    setError(null);
  }, [templateId]);

  /**
   * Update a single card's configuration
   */
  const updateCard = useCallback((cardId, chartDefinition) => {
    setCardChanges(prev => ({
      ...prev,
      [cardId]: chartDefinition
    }));
    setError(null);
  }, []);

  /**
   * Save all changes to backend
   */
  const saveChanges = useCallback(async () => {
    if (!currentDashboardId) {
      throw new Error('No dashboard selected for editing');
    }

    setSaveInProgress(true);
    setError(null);

    try {
      const promises = [];

      // Save layout changes
      if (layoutChanges) {
        promises.push(
          updateCardLayout(
            currentDashboardId,
            layoutChanges.card_order,
            layoutChanges.template_id
          )
        );
      }

      // Save card configuration changes
      Object.entries(cardChanges).forEach(([cardId, chartDefinition]) => {
        promises.push(
          updateCardConfig(currentDashboardId, cardId, chartDefinition)
        );
      });

      // Execute all saves in parallel
      await Promise.all(promises);

      // Clear changes after successful save
      setLayoutChanges(null);
      setCardChanges({});

      // Trigger refresh callback instead of page reload
      if (onSaveComplete) {
        onSaveComplete(currentDashboardId);
      }

    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to save changes';
      setError(errorMessage);
      throw err;
    } finally {
      setSaveInProgress(false);
    }
  }, [currentDashboardId, layoutChanges, cardChanges, onSaveComplete]);

  /**
   * Cancel changes and revert to original
   */
  const cancelChanges = useCallback(() => {
    if (hasUnsavedChanges()) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to discard them?'
      );
      if (!confirmed) {
        return false;
      }
    }

    // Reset changes
    setLayoutChanges(null);
    setCardChanges({});
    setError(null);
    return true;
  }, [hasUnsavedChanges]);

  /**
   * Reset dashboard to default configuration
   */
  const resetToDefault = useCallback(async (resetType = 'all', userId = 'global') => {
    if (!currentDashboardId) {
      throw new Error('No dashboard selected');
    }

    const confirmed = window.confirm(
      `Reset dashboard to default? This will delete ${resetType === 'all' ? 'all customizations' : resetType}.`
    );
    if (!confirmed) {
      return;
    }

    setSaveInProgress(true);
    setError(null);

    try {
      await resetDashboardCustomizations(currentDashboardId, resetType, userId);

      // Clear local changes
      setLayoutChanges(null);
      setCardChanges({});

      // Trigger refresh callback instead of page reload
      if (onSaveComplete) {
        onSaveComplete(currentDashboardId);
      }

    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to reset dashboard';
      setError(errorMessage);
      throw err;
    } finally {
      setSaveInProgress(false);
    }
  }, [currentDashboardId, onSaveComplete]);

  /**
   * Get merged dashboard configuration (base + customizations)
   */
  const getCustomizedConfig = useCallback(async (dashboardId, userId = 'global') => {
    try {
      const config = await getCustomizedDashboard(dashboardId, userId);
      return config;
    } catch (err) {
      console.error('Failed to fetch customized dashboard:', err);
      throw err;
    }
  }, []);

  const value = {
    // State
    isEditMode,
    currentDashboardId,
    templateId,
    layoutChanges,
    cardChanges,
    originalConfig,
    saveInProgress,
    error,
    hasUnsavedChanges: hasUnsavedChanges(),

    // Actions
    enterEditMode,
    exitEditMode,
    updateLayout,
    updateCard,
    saveChanges,
    cancelChanges,
    resetToDefault,
    getCustomizedConfig
  };

  return (
    <EditModeContext.Provider value={value}>
      {children}
    </EditModeContext.Provider>
  );
};

EditModeProvider.propTypes = {
  children: PropTypes.node.isRequired,
  onSaveComplete: PropTypes.func
};

export default EditModeContext;
